// Copyright (c) Docugami, Inc. All rights reserved.

import { EntityState, createEntityAdapter, createSelector, isAnyOf, nanoid } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { DocSetStatus } from "components/cluster_management/DocSetStatusTypes";
import { createFolderIdString } from "components/file_management/file_helpers";
import { Config } from "config";
import { debounce } from "lodash";
import { OrganizationSignalRConnectionInstance, waitForSignalRConnection } from "networking/OrganizationSignalRConnection";
import { ProviderId } from "networking/StorageProviders";
import { ClusterInfo, DocsetFeature, LabelRemovedMessage, TokenRange } from "networking/api";
import { BindEntryAction, BindingProviderId, ReportBinding } from "networking/reportsApi";
import type { AppDispatch, AppState } from "store";
import { createDgBaseQuery } from "store/baseQuery";
import { TextEditOperation, chunkEntityAdapter, dgmlSlice } from "store/dgml/dgmlSlice";
import { startAppListening } from "store/listenerMiddleware";
import { sdvSlice, setSelectedId } from "store/sdvSlice";
import { TokenKeyRange } from "utils/MappedTokens";
import { SpanIdUtils } from "utils/SpanIdUtils";
import type { LabelSchema } from "./frontendTypes";

// Re-export types for backwards compatibility (types moved to frontendTypes.ts)
export type { ChildLabelReference, LabelSchema } from "./frontendTypes";

// Lazy-loaded to avoid circular dependency - reportsSlice may import modules that import frontendSlice
// Note: selectLabelState is NOT imported from ./selectors to avoid circular dependency
// The selector logic is inlined where needed (see line ~1351)
const getReportsSlice = () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require("store/reports/reportsSlice") as typeof import("store/reports/reportsSlice");
};

// Lazy-loaded to avoid circular dependency - documentDgml imports from frontendSlice
const getDgmlInstancesSingleton = () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require("hooks/documentDgml").DgmlInstancesSingleton as typeof import("hooks/documentDgml").DgmlInstancesSingleton;
};

export const labelEntityAdapter = createEntityAdapter<LabelSchema>({
    selectId: x => x.id!
});

export const createExampleChunkEntityId = (label: string, spanId: string) => `${label.toLowerCase()}|${spanId}`
export const exampleChunkEntityAdapter = createEntityAdapter<ExampleChunk>({
    selectId: x => createExampleChunkEntityId(x.label, x.spanId)
})

export const createQualityControlWarningEntityId = (label: string, spanId: string) => `${label.toLowerCase()}|${spanId}`
export const qualityControlWarningEntityAdapter = createEntityAdapter<QualityControlWarning>({
    selectId: x => createQualityControlWarningEntityId(x.label, x.spanId)
})

const feedbackLockAdapter = createEntityAdapter<FeedbackLock>({
    selectId: x => x.id
})

export const getFeedbackLockId = (fileId: string, labelId: string) => `${fileId}:${labelId}`;

const propertyDefinitionAdapter = createEntityAdapter<PropertyDefinition>({
    selectId: x => x.id
});

const propertyInstanceAdapter = createEntityAdapter<PropertyInstance>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllProperties,
    selectById: selectPropertyById,
} = propertyInstanceAdapter.getSelectors();

const baseQuery = createDgBaseQuery(`${Config.apiUrl}`, {
    paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
            const value = params[key];
            if (value !== undefined) {
                if (Array.isArray(value)) {
                    value.forEach((val) => searchParams.append(key, val));
                } else {
                    searchParams.append(key, value);
                }
            }
        }
        return searchParams.toString();
    },
});

// Types and functions needed by createApi - must be defined before createApi call
interface TokenRangeDtoInternal {
    fromToken: number;
    toToken: number;
}

interface PropertyInstanceRawInternal {
    id: string;
    fileId: string;
    label: string;
    labelId: string;
    value: string;
    tokenRanges?: TokenRangeDtoInternal[];
    pageNumber?: number;
}

const createPropertyInstance = (property: PropertyInstanceRawInternal): PropertyInstance => ({
    ...property,
    tokenRanges: property.tokenRanges?.map(tr => ({
        fromTok: tr.fromToken,
        toTok: tr.toToken
    }))
});

export const frontendSlice = createApi({
    reducerPath: 'frontend',
    tagTypes: ['feedback-status', 'labels', 'example-chunks', 'docset-status', 'storage-providers', 'docset-documents', 'directory-files', 'providerRootDirectory', 'quality-control-warnings', 'docset-description', 'label-qc-settings', 'model-stats', 'feedback-locks', 'properties'],
    baseQuery: baseQuery,
    endpoints: builder => ({
        health: builder.query<boolean, void>({
            query: () => ({
                url: '/healthz',
                responseHandler: 'text'
            }),
            transformResponse: (response: string, meta, request) => response === 'Healthy'
        }),
        getLabels: builder.query<EntityState<LabelSchema>, string>({
            query: (docsetId: string) => ({
                url: `/feedback/docset/${encodeURIComponent(docsetId)}/labels`
            }),
            providesTags: (result, error, docsetId) => [{ type: 'labels', id: docsetId }],
            transformResponse: (result: LabelSchema[], meta, request) => labelEntityAdapter.setAll(labelEntityAdapter.getInitialState(), result)
        }),
        addLabel: builder.mutation<void, LabelRequest>({
            query: (request: LabelRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/label`,
                method: 'POST',
                body: request
            }),
            invalidatesTags: (result, error, arg) => [{ type: "labels", id: arg.docsetId }]
        }),
        renameLabel: builder.mutation<void, RenameLabelRequest>({
            query: (request: RenameLabelRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/label/${encodeURIComponent(request.labelId)}`,
                method: 'PUT',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const patch = api.dispatch(frontendSlice.util.updateQueryData("getLabels", request.docsetId, x =>
                    labelEntityAdapter.updateOne(x, {
                        id: request.labelId,
                        changes: {
                            label: request.label
                        }
                    })
                ));

                try {
                    await api.queryFulfilled;
                }
                catch {
                    patch.undo();
                }
            }
        }),
        labelChunk: builder.mutation<void, LabelChunkRequest>({
            query: (request: LabelChunkRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/label`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const patches = api.dispatch(dgmlSlice.util.updateQueryData("getDgmlNodes", { docsetId: request.docsetId, fileId: request.fileId }, x => {
                    const existing = x.chunks.entities[request.spanId] ?? getDgmlInstancesSingleton().getInstanceById(createFolderIdString(request.docsetId, request.fileId), request.spanId)!.chunk!
                    if (!!request.label) {
                        existing.labels = [{ toolId: 'cp', value: request.label, confidence: 1 }, ...existing.labels];
                    }

                    chunkEntityAdapter.upsertOne(x.chunks, existing);
                }));

                try {
                    await api.queryFulfilled;
                }
                catch {
                    patches.undo();
                }
            }
        }),
        labelChunks: builder.mutation<void, LabelChunksRequest>({
            query: (request: LabelChunksRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/labels`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const patches = api.dispatch(dgmlSlice.util.updateQueryData("getDgmlNodes", { docsetId: request.docsetId, fileId: request.fileId }, x => {
                    for (const spanId of request.spanIds) {
                        const existing = x.chunks.entities[spanId] ?? getDgmlInstancesSingleton().getInstanceById(createFolderIdString(request.docsetId, request.fileId), spanId)!.chunk!
                        if (!!request.label) {
                            existing.labels = [{ toolId: 'cp', value: request.label, confidence: 1 }, ...existing.labels];
                        }

                        chunkEntityAdapter.upsertOne(x.chunks, existing);
                    }
                }));

                try {
                    await api.queryFulfilled;
                }
                catch {
                    patches.undo();
                }
            }
        }),
        removeLabel: builder.mutation<void, LabelChunkRequest>({
            query: (request: LabelChunkRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/label`,
                method: 'DELETE',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                await api.queryFulfilled;
                api.dispatch(dgmlSlice.util.updateQueryData("getDgmlNodes", { docsetId: request.docsetId, fileId: request.fileId }, x => {
                    const existing = x.chunks.entities[request.spanId];
                    if (!!existing) {
                        existing.labels = existing.labels.filter(x => x.value !== request.label);
                    }
                }));
            }
        }),
        removeLabels: builder.mutation<void, RemoveLabelsRequest>({
            query: (request: RemoveLabelsRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/labels`,
                method: 'DELETE',
                body: request
            })
        }),
        resize: builder.mutation<void, ResizeChunkRequest>({
            query: (request: ResizeChunkRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/resize`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (request, api) => {
                const patches = api.dispatch(dgmlSlice.util.updateQueryData("getDgmlNodes", { docsetId: request.docsetId, fileId: request.fileId }, x => {
                    const existing = x.chunks.entities[request.spanId];

                    const folderId = createFolderIdString(request.docsetId, request.fileId);
                    let subtreeId;
                    if (!!existing) {
                        subtreeId = SpanIdUtils.parse(existing.spanId).subtreeId;
                    }

                    const targetSpanId = SpanIdUtils.create(request.fileId, request.tokenKeyRange.fromTokenKey, request.tokenKeyRange.toTokenKey, subtreeId)
                    const target = getDgmlInstancesSingleton().getInstanceById(folderId, targetSpanId.value)!.chunk!;
                    target.labels = existing?.labels || [];

                    chunkEntityAdapter.upsertOne(x.chunks, target);
                }));

                try {
                    await api.queryFulfilled;
                }
                catch {
                    patches.undo();
                }
            }
        }),
        editText: builder.mutation<TextEditOperation, EditTextRequest>({
            query: (request: EditTextRequest) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}/edit-text`,
                method: 'POST',
                body: request
            })
        }),
        getFeedbackStatus: builder.query<FeedbackResponse, FeedbackRequest>({
            query: request => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/status`,
                params: !!request.version ? { "version": request.version } : undefined
            }),
            providesTags: () => ['feedback-status'],
            onCacheEntryAdded: async (arg, api) => {
                await api.cacheDataLoaded;

                const update = debounce(() => api.dispatch(frontendSlice.util.invalidateTags(["feedback-status"])), 3000);
                const handleUpdate = async (payload: FeedbackWatermarksChangedMessage) => {
                    if (arg.docsetId !== payload.docsetId) {
                        return;
                    }

                    update();
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("feedbackWatermarksChanged", handleUpdate);
                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("feedbackWatermarksChanged", handleUpdate);
                }
            }
        }),
        getPageImage: builder.query<string, PageImageRequest>({
            query: request => ({
                url: `${Config.dgmlUrl}/v1preview1/documents/${encodeURIComponent(request.fileId)}/pages/${encodeURIComponent(request.page)}/content`,
                responseHandler: async (response) => {
                    return URL.createObjectURL(await response.blob());
                },
            }),
            onCacheEntryAdded: async (request, api) => {
                const { data } = await api.cacheDataLoaded;
                await api.cacheEntryRemoved;
                if (!!data) {
                    URL.revokeObjectURL(data);
                }
            },
            keepUnusedDataFor: 600
        }),
        getDocumentPagesSpriteSheet: builder.query<string, string>({
            query: request => ({
                url: `documents/${encodeURIComponent(request)}/page/sprites`,
                responseHandler: async (response) => {
                    return URL.createObjectURL(await response.blob());
                },
            }),
            onCacheEntryAdded: async (request, api) => {
                const { data } = await api.cacheDataLoaded;
                await api.cacheEntryRemoved;
                if (!!data) {
                    URL.revokeObjectURL(data);
                }
            },
            keepUnusedDataFor: 600
        }),
        getAffectedItemsForFilePaths: builder.mutation<AffectedItemsForFilePathsResponse, AffectedItemsForFilePathsRequest>({
            query: request => ({
                url: `documents/reassign-effects`,
                method: 'POST',
                body: request.filePaths
            })
        }),
        getExampleChunks: builder.query<EntityState<ExampleChunk>, ListExampleChunksRequest>({
            query: (request: ListExampleChunksRequest) => {
                return {
                    url: `/feedback/docset/${request.docsetId}/examples`,
                    method: 'GET',
                    params: {
                        label: request.label,
                        fileId: request.fileId,
                        includeText: request.includeText
                    }
                }
            },
            providesTags: (result, error, arg) => [
                'example-chunks',
                { type: 'example-chunks', id: !!arg.includeText ? 'Text' : 'NoText' },
                ...(result?.ids ?? []).map(x => ({ type: 'example-chunks' as const, id: x.toString() }))
            ],
            transformResponse: (response: ListExampleChunksResponse, meta, arg): EntityState<ExampleChunk> => {
                return exampleChunkEntityAdapter.setAll(
                    exampleChunkEntityAdapter.getInitialState(),
                    response.examples);
            },
            onCacheEntryAdded: async (request, api) => {
                await api.cacheDataLoaded;

                const handleRemove = async (message: ExampleChunksRemovedMessage) => {
                    if (message.docsetId !== request.docsetId) {
                        return;
                    }

                    api.updateCachedData(x => {
                        exampleChunkEntityAdapter.removeOne(x, createExampleChunkEntityId(message.label, message.spanId))
                    });
                }

                const handleChange = async (message: ExampleChunksChangedMessage) => {
                    if (message.item.docsetId !== request.docsetId) {
                        return;
                    }

                    if (!request.label || request.label.toLocaleLowerCase() === message.item.label.toLocaleLowerCase()) {
                        if (!!request.includeText) {
                            // Need to fetch the preview
                            try {
                                const response = await api.dispatch(frontendSlice.endpoints.getExampleChunk.initiate({ docsetId: request.docsetId, id: message.item.id, includeText: true })).unwrap();
                                api.updateCachedData(x => {
                                    exampleChunkEntityAdapter.upsertOne(x, response);
                                })
                            }
                            catch {
                                api.dispatch(frontendSlice.util.invalidateTags([{ type: 'example-chunks', id: 'Text' }]))
                            }
                        }
                        else {
                            // Can use the payload in the message
                            api.updateCachedData(x => {
                                exampleChunkEntityAdapter.upsertOne(x, message.item)
                            });
                        }
                    }
                }

                const handle = (message: ExampleChunksChangedMessage | ExampleChunksRemovedMessage) =>
                    isExampleRemovedMessage(message) ? handleRemove(message) : handleChange(message);

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("exampleChunksChanged", handle);
                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("exampleChunksChanged", handle);
                }
            }
        }),
        getExampleChunk: builder.mutation<ExampleChunk, GetExampleChunkRequest>({
            query: (request: GetExampleChunkRequest) => ({
                url: `/feedback/docset/${request.docsetId}/examples/${encodeURIComponent(request.id)}`,
                method: 'GET',
                params: !!request.includeText ? { includeText: true } : undefined
            })
        }),
        addExampleChunk: builder.mutation<ExampleChunk, AddExampleChunkRequest>({
            query: (request: AddExampleChunkRequest) => ({
                url: `/feedback/docset/${request.docsetId}/examples`,
                method: 'POST',
                body: request
            }),
            onQueryStarted: async (arg, api) => {
                const tempId = nanoid();
                const { undo } = api.dispatch(frontendSlice.util.updateQueryData("getExampleChunks", { docsetId: arg.docsetId, includeText: false }, state => {
                    exampleChunkEntityAdapter.addOne(state, {
                        id: tempId,
                        docsetId: arg.docsetId,
                        fileId: arg.fileId,
                        spanId: arg.spanId,
                        label: arg.label,
                        tokenRange: { fromTok: 0, toTok: 0 },
                        richText: undefined
                    })
                }));
                try {
                    const response = await api.queryFulfilled;
                    api.dispatch(frontendSlice.util.updateQueryData("getExampleChunks", { docsetId: arg.docsetId, includeText: false }, state => {
                        exampleChunkEntityAdapter.removeOne(state, tempId);
                        exampleChunkEntityAdapter.upsertOne(state, response.data)
                    }))
                }
                catch {
                    undo();
                }
            }
        }),
        removeExampleChunk: builder.mutation<void, ExampleChunk>({
            query: (chunk: ExampleChunk) => ({
                url: `feedback/docset/${encodeURIComponent(chunk.docsetId)}/examples/${encodeURIComponent(chunk.id)}`,
                method: 'DELETE'
            }),
            onQueryStarted: async (arg, api) => {
                const { undo: undoGlobal } = api.dispatch(frontendSlice.util.updateQueryData("getExampleChunks", { docsetId: arg.docsetId, includeText: false }, state => {
                    exampleChunkEntityAdapter.removeOne(state, createExampleChunkEntityId(arg.label, arg.spanId))
                }))

                const { undo: undoLabel } = api.dispatch(frontendSlice.util.updateQueryData("getExampleChunks", { docsetId: arg.docsetId, label: arg.label, includeText: true }, state => {
                    exampleChunkEntityAdapter.removeOne(state, createExampleChunkEntityId(arg.label, arg.spanId))
                }))

                try {
                    await api.queryFulfilled;
                }
                catch {
                    undoGlobal();
                    undoLabel();
                }
            }
        }),
        getQualityControlWarnings: builder.query<EntityState<QualityControlWarning>, ListQualityControlWarningsRequest>({
            query: (request: ListQualityControlWarningsRequest) => {
                return {
                    url: `/feedback/docset/${request.docsetId}/document/${request.fileId}/warnings`,
                    method: 'GET'
                }
            },
            providesTags: (result, error, request) => [{ type: 'quality-control-warnings', id: `${request.docsetId}:${request.fileId}` }],
            transformResponse: (response: ListQualityControlWarningsResponse, meta, arg): EntityState<QualityControlWarning> => {
                return qualityControlWarningEntityAdapter.setAll(
                    qualityControlWarningEntityAdapter.getInitialState(),
                    response.warnings);
            }
        }),
        getStorageProviders: builder.query<ProviderIdsResponse, void>({
            query: () => ({
                url: "/documents/storage-providers",
                method: 'GET'
            }),
            providesTags: ['storage-providers'],

        }),
        getDocSetStatus: builder.query<DocSetStatus, string>({
            query: docsetId => ({
                url: `/Cluster/${encodeURIComponent(docsetId)}/status`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'docset-status', id: arg }]
        }),
        createBugReport: builder.mutation<void, BugReportRequest>({
            query: (request: BugReportRequest) => ({
                url: `/bugreport/docset/${encodeURIComponent(request.docsetId)}/document/${encodeURIComponent(request.fileId)}`,
                method: 'POST',
                body: request
            }),
        }),
        activityPing: builder.query<void, string | undefined>({
            query: (docsetId) => ({
                url: `/activity`,
                method: 'POST',
                body: !!docsetId ? { docsetId: docsetId } : undefined
            }),
            keepUnusedDataFor: 0
        }),
        mergeDocsets: builder.mutation<ClusterInfo, MergeDocsetsRequest>({
            query: request => ({
                url: `/Cluster/${encodeURIComponent(request.targetDocsetId)}/merge`,
                method: 'POST',
                body: {
                    clusterIds: request.docsetIds
                }
            })
        }),
        getDocsetDescription: builder.query<DocsetDescriptionResponse, string>({
            query: (docsetId) => ({
                url: `/classification/docset/${encodeURIComponent(docsetId)}/description`,
                method: 'GET'
            }),
            keepUnusedDataFor: 0,
            providesTags: (result, error, arg) => [{ type: 'docset-description', id: arg }],
        }),
        updateDocsetDescription: builder.mutation<DocsetDescriptionResponse, UpdateDocsetDescriptionRequest>({
            query: request => ({
                url: `/classification/docset/${encodeURIComponent(request.docsetId)}/description`,
                method: 'POST',
                body: {
                    description: request.description
                }
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'docset-description', id: arg.docsetId }]
        }),
        deleteDocsetDescription: builder.mutation<DocsetDescriptionResponse, string>({
            query: request => ({
                url: `/classification/docset/${encodeURIComponent(request)}/description`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'docset-description', id: arg }]
        }),
        updateDocsetFeatures: builder.mutation<ClusterInfo, UpdateDocsetFeaturesRequest>({
            query: request => ({
                url: `${Config.apiUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}`,
                method: 'PATCH',
                body: {
                    features: request.features
                }
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'docset-status', id: arg.docsetId }]
        }),
        getLabelQualityControlSettings: builder.query<LabelQualityControlSettings, LabelQualityControlRequest>({
            query: (request) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/labels/${encodeURIComponent(request.labelId)}/quality-control`,
                method: 'GET'
            }),
            keepUnusedDataFor: 0,
            providesTags: (result, error, arg) => [{ type: 'label-qc-settings', id: `qc:settings:${arg.docsetId}:${arg.labelId}` }],
        }),
        updateLabelQualityControlSettings: builder.mutation<LabelQualityControlSettings, UpdateLabelQualityControlSettingsRequest>({
            query: request => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/labels/${encodeURIComponent(request.labelId)}/quality-control`,
                method: 'POST',
                body: {
                    labelId: request.labelId,
                    description: request.description,
                    dataType: request.dataType,
                    isNullable: request.isNullable,
                    enabled: request.enabled
                }
            }),
            invalidatesTags: (result, error, arg) => ['quality-control-warnings', { type: 'label-qc-settings', id: `qc:settings:${arg.docsetId}:${arg.labelId}` }]
        }),
        deleteLabelQualityControlUserDescription: builder.mutation<LabelQualityControlSettings, LabelQualityControlRequest>({
            query: request => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/labels/${encodeURIComponent(request.labelId)}/quality-control`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'label-qc-settings', id: `qc:settings:${arg.docsetId}:${arg.labelId}` }]
        }),
        getLiveQaModelStats: builder.query<DocSetModelStatsResponse, string>({
            query: (request) => ({
                url: `/liveqa/${encodeURIComponent(request)}/stats`,
                method: 'GET'
            }),
            providesTags: (result, error, arg) => [{ type: 'model-stats', id: arg }],
        }),
        getFeedbackLocks: builder.query<EntityState<FeedbackLock>, string>({
            query: (docsetId) => ({
                url: `/feedback/docset/${encodeURIComponent(docsetId)}/locks`,
                params: {
                    activeFilesOnly: true
                }
            }),
            providesTags: (result, error, arg) => [{ type: 'feedback-locks', id: arg }],
            transformResponse: (result: GetFeedbackLocksResponse, meta, request): EntityState<FeedbackLock> => {
                return feedbackLockAdapter.setAll(
                    feedbackLockAdapter.getInitialState(),
                    result.items);
            },
            onCacheEntryAdded: async (docsetId, api) => {
                const update = debounce(() => api.dispatch(frontendSlice.util.invalidateTags(["feedback-locks"])), 1500, { leading: true, maxWait: 1500, trailing: true });
                const handleUpdate = (payload: FeedbackLocksChangedMessage) => {
                    if (payload.docsetId !== docsetId) {
                        return;
                    }

                    update();
                }

                const signalRInstance = await waitForSignalRConnection();
                signalRInstance.connection.on("feedbackLocksChanged", handleUpdate);

                try {
                    await api.cacheEntryRemoved;
                }
                finally {
                    OrganizationSignalRConnectionInstance?.connection.off("feedbackLocksChanged", handleUpdate);
                }
            }
        }),
        lockFeedback: builder.mutation<void, LockFeedbackRequest>({
            query: (request) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/locks`,
                method: 'POST',
                body: {
                    items: request.items
                }
            }),
            onQueryStarted: async (request, api) => {
                await api.queryFulfilled;

                // There could be feedback lock items that triggered the creation of a new label schema item
                if (!!request.items.find(item => !item.labelId)) {
                    api.dispatch(frontendSlice.util.invalidateTags([{ type: "labels", id: request.docsetId }]));
                }
            }
        }),
        unlockFeedback: builder.mutation<void, UnlockFeedbackRequest>({
            query: (request) => ({
                url: `/feedback/docset/${encodeURIComponent(request.docsetId)}/locks`,
                body: {
                    items: request.items
                },
                method: 'DELETE'
            })
        }),
        getFeedbackLock: builder.query<FeedbackLock, { docsetId: string; fileId: string; labelId: string }>({
            query: ({ docsetId, fileId, labelId }) => ({
                url: `/feedback/docset/${encodeURIComponent(docsetId)}/locks/${encodeURIComponent(getFeedbackLockId(fileId, labelId))}`,
                method: 'GET'
            })
        }),
        listPropertyDefinitions: builder.query<EntityState<PropertyDefinition>, { docsetId: string }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties`,
                method: 'GET'
            }),
            providesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }],
            transformResponse: (response: ListPropertyDefinitionsResponse, meta, request): EntityState<PropertyDefinition> => {
                return propertyDefinitionAdapter.setAll(
                    propertyDefinitionAdapter.getInitialState(),
                    response.properties);
            }
        }),
        getPropertyDefinition: builder.query<PropertyDefinition, { docsetId: string; propertyId: string }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties/${encodeURIComponent(request.propertyId)}`,
                method: 'GET'
            }),
            providesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }, { type: 'properties', id: request.propertyId }],
        }),
        createPropertyDefinition: builder.mutation<PropertyDefinition, { docsetId: string; body: CreatePropertyDefinitionRequest }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties`,
                method: 'POST',
                body: request.body
            }),
            invalidatesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }],
        }),
        updatePropertyDefinition: builder.mutation<void, { docsetId: string; propertyId: string; body: UpdatePropertyDefinitionRequest }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties/${encodeURIComponent(request.propertyId)}`,
                method: 'PUT',
                body: request.body
            }),
            invalidatesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }, { type: 'properties', id: request.propertyId }]
        }),
        deleteProperty: builder.mutation<void, { docsetId: string; propertyId: string }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties/${encodeURIComponent(request.propertyId)}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }, { type: 'properties', id: request.propertyId }]
        }),
        listDocumentProperties: builder.query<EntityState<PropertyInstance>, { docsetId: string; fileId: string }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/document-properties/${encodeURIComponent(request.fileId)}`,
                method: 'GET'
            }),
            providesTags: (result, error, request) => [{ type: 'properties', id: request.docsetId }, { type: 'properties', id: request.fileId }],
            transformResponse: (response: ListDocumentPropertiesResponse, meta, request): EntityState<PropertyInstance> => {
                return propertyInstanceAdapter.setAll(
                    propertyInstanceAdapter.getInitialState(),
                    response.properties.map(createPropertyInstance)
                );
            }
        }),
        getDocumentProperty: builder.query<PropertyInstance, { docsetId: string; propertyId: string; fileId: string }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties/${encodeURIComponent(request.propertyId)}/documents/${encodeURIComponent(request.fileId)}`,
                method: 'GET'
            }),
            transformResponse: createPropertyInstance,
            providesTags: (result, error, request) => [
                { type: 'properties', id: request.docsetId },
                { type: 'properties', id: request.propertyId },
                { type: 'properties', id: request.fileId }
            ]
        }),
        setPropertyValue: builder.mutation<PropertyInstance, { docsetId: string; propertyId: string; fileId: string; body: SetPropertyValueRequest }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/properties/${encodeURIComponent(request.propertyId)}/documents/${encodeURIComponent(request.fileId)}`,
                method: 'PUT',
                body: {
                    ...request.body,
                    tokenRanges: !!request.body.tokenRanges?.length ? request.body.tokenRanges?.map(range => ({
                        fromToken: range.fromTok,
                        toToken: range.toTok
                    } as TokenRangeDto)) : undefined,
                }
            }),
            transformResponse: createPropertyInstance,
            invalidatesTags: (result, error, request) => [
                { type: 'properties', id: request.docsetId },
                { type: 'properties', id: request.propertyId },
                { type: 'properties', id: request.fileId }
            ]
        }),
        createDocumentProperty: builder.mutation<PropertyInstance, { docsetId: string; fileId: string; body: CreateDocumentPropertyRequest }>({
            query: (request) => ({
                url: `${Config.dgmlUrl}/v1preview1/docsets/${encodeURIComponent(request.docsetId)}/document-properties/${encodeURIComponent(request.fileId)}`,
                method: 'POST',
                body: {
                    ...request.body,
                    tokenRanges: !!request.body.tokenRanges?.length ? request.body.tokenRanges?.map(range => ({
                        fromToken: range.fromTok,
                        toToken: range.toTok
                    } as TokenRangeDto)) : undefined,
                }
            }),
            transformResponse: createPropertyInstance,
            invalidatesTags: (result, error, request) => [
                { type: 'properties', id: request.docsetId },
                { type: 'properties', id: request.fileId }
            ]
        }),
        getLabelAttributions: builder.query<string[], { docsetId: string; labelId: string; }>({
            query: (request) => `/feedback/docset/${encodeURIComponent(request.docsetId)}/labels/${encodeURIComponent(request.labelId)}/attributions`,
        }),
    })
});

export const selectFeedbackLocksSelector = frontendSlice.endpoints.getFeedbackLocks.select;
export const selectGetFeedbackStatus = (state: AppState, docsetId: string, version?: number) => frontendSlice.endpoints.getFeedbackStatus.select({ docsetId, version })(state).data;

export interface PropertyDefinition {
    id: string;
    label: string;
    labelId: string;
}

export interface CreatePropertyDefinitionRequest {
    label: string;
}

export interface UpdatePropertyDefinitionRequest {
    label: string;
}

export interface ListPropertyDefinitionsResponse {
    properties: PropertyDefinition[];
}

export interface SetPropertyValueRequest {
    value?: string;
    tokenRanges?: TokenRange[];
    pageNumber?: number;
}

interface TokenRangeDto {
    fromToken: number;
    toToken: number;
}

export interface CreateDocumentPropertyRequest {
    label: string;
    value: string;
    tokenRanges?: TokenRange[];
    pageNumber?: number;
}

export interface PropertyInstance {
    id: string;
    fileId: string;
    label: string;
    labelId: string;
    value: string;
    tokenRanges?: TokenRange[];
    pageNumber?: number;
}

interface PropertyInstanceRaw {
    id: string;
    fileId: string;
    label: string;
    labelId: string;
    value: string;
    tokenRanges?: TokenRangeDto[];
    pageNumber?: number;
}

export interface ListDocumentPropertiesResponse {
    properties: PropertyInstanceRaw[];
}

export const selectFeedbackLocks = frontendSlice.endpoints.getFeedbackLocks.select;

export interface PageImageRequest {
    fileId: string;
    page: number
}

export const documentPagesSpriteSheetSelector = createSelector([
    (folderId: string) => folderId,
],
    (folderId) => frontendSlice.endpoints.getDocumentPagesSpriteSheet.select(folderId)
);

export const pageImageSelector = createSelector([
    (fileId: string, page: number) => fileId,
    (fileId: string, page: number) => page,

],
    (fileId, page) => frontendSlice.endpoints.getPageImage.select({ fileId: fileId, page: page }),
    {
        memoizeOptions: {
            maxSize: 25
        }
    }
);

export const {
    useHealthQuery,
    useGetLabelsQuery,
    useAddLabelMutation,
    useRenameLabelMutation,
    useLabelChunkMutation,
    useLabelChunksMutation,
    useRemoveLabelMutation,
    useRemoveLabelsMutation,
    useResizeMutation,
    useEditTextMutation,
    useGetFeedbackStatusQuery,
    useGetAffectedItemsForFilePathsMutation,
    useGetPageImageQuery,
    useGetDocumentPagesSpriteSheetQuery,
    useGetExampleChunksQuery,
    useGetQualityControlWarningsQuery,
    useAddExampleChunkMutation,
    useRemoveExampleChunkMutation,
    useGetStorageProvidersQuery,
    useGetDocSetStatusQuery,
    useCreateBugReportMutation,
    useActivityPingQuery,
    useMergeDocsetsMutation,
    useGetDocsetDescriptionQuery,
    useUpdateDocsetDescriptionMutation,
    useDeleteDocsetDescriptionMutation,
    useUpdateDocsetFeaturesMutation,
    useGetLabelQualityControlSettingsQuery,
    useUpdateLabelQualityControlSettingsMutation,
    useDeleteLabelQualityControlUserDescriptionMutation,
    useGetLiveQaModelStatsQuery,
    useGetFeedbackLocksQuery,
    useLockFeedbackMutation,
    useUnlockFeedbackMutation,
    useGetFeedbackLockQuery,
    useDeletePropertyMutation,
    useListDocumentPropertiesQuery,
    useGetDocumentPropertyQuery,
    useSetPropertyValueMutation,
    useCreateDocumentPropertyMutation,
    useGetLabelAttributionsQuery
} = frontendSlice;

export const {
    selectAll: selectAllExampleChunks,
    selectById: selectExampleChunkByEntityId
} = exampleChunkEntityAdapter.getSelectors();

export interface LabelRequest {
    docsetId: string;
    label: string;
}

export interface RenameLabelRequest {
    docsetId: string;
    label: string;
    labelId: string;
}

export interface LabelChunkRequest {
    docsetId: string;
    fileId: string;
    spanId: string;
    label?: string;
    dgmlVersion?: string;
}

export interface LabelChunksRequest extends Omit<LabelChunkRequest, 'spanId'> {
    spanIds: string[];
    labelId?: string;
}

export interface RemoveLabelsRequest extends Omit<LabelChunkRequest, 'spanId'> {
    spanIds: string[];
    labelId?: string;
}

export interface ResizeChunkRequest {
    docsetId: string;
    fileId: string;
    spanId: string;
    tokenKeyRange: TokenKeyRange;
    dgmlVersion: string;
}

export interface EditTextRequest {
    docsetId: string;
    fileId: string;
    tokenKeyRange: TokenKeyRange;
    text: string;
}

export interface FeedbackRequest {
    docsetId: string;
    version?: number;
}

export interface FeedbackResponse {
    docsetId: string;
    version?: number;
    feedback?: FeedbackItem[];
    labelIds?: string[];
    trainedVersions?: Record<string, number>;
}

export interface GetExampleChunkRequest {
    docsetId: string;
    id: string;
    includeText?: boolean;
}

export interface FeedbackItem {
    fileId: string;
    spanId?: string;
    labelId: string;
    version: number;
}

export interface AffectedItemsForFilePathsRequest {
    filePaths: string[];
}

export interface AffectedItemsForFilePathsResponse {
    effects: ReassignmentEffect[];
}

export interface ReassignmentEffect {
    id: string;
    name: string;
    itemType: ReassignmentEffectItemType;
}

export interface MergeDocsetsRequest {
    targetDocsetId: string;
    docsetIds: string[];
}

export interface UpdateDocsetDescriptionRequest {
    docsetId: string;
    description: string;
}

export interface UpdateDocsetFeaturesRequest {
    docsetId: string;
    features: DocsetFeature;
}

export enum ReassignmentEffectItemType {
    DocSet = "DocSet",
    WorkPath = "WorkPath"
}

export interface AddExampleChunkRequest {
    docsetId: string;
    fileId: string;
    spanId: string;
    label: string;
    dgmlVersion?: string;
}

export interface ExampleChunk {
    id: string;
    docsetId: string;
    fileId: string;
    label: string;
    spanId: string;
    tokenRange: TokenRange;
    richText?: string;
}

export interface QualityControlWarning {
    id: string;
    docsetId: string;
    fileId: string;
    label: string;
    labelId: string;
    spanId: string;
    reasoning: string;
}

export interface ProviderIdsResponse {
    providers: ProviderId[];
}

export interface ListExampleChunksRequest {
    docsetId: string;
    fileId?: string;
    label?: string;
    includeText?: boolean;
}

export interface ListQualityControlWarningsRequest {
    docsetId: string;
    fileId: string;
}

export enum BugType {
    Table = "Table",
    Form = "Form",
    VLA = "VLA"
}

export interface BugReportRequest {
    docsetId: string;
    fileId: string;
    pageNum: number;
    type: BugType;
}

export interface FeedbackLock {
    id: string;
    fileId: string;
    labelId: string;
    isUser?: boolean;
}

export interface LockFeedbackRequest {
    docsetId: string;
    items: FeedbackLockInput[];
}

export interface FeedbackLockInput {
    fileId: string;
    labelId: string;
    label?: string;
    spanIds: string[];
}

export interface UnlockFeedbackRequest {
    docsetId: string;
    items: FeedbackReference[];
}

export interface FeedbackReference {
    fileId: string;
    labelId: string;
}

export interface FeedbackLocksChangedMessage {
    docsetId: string;
    locksAdded: FeedbackReference[];
    locksRemoved: FeedbackReference[];
}

interface GetFeedbackLocksResponse {
    items: FeedbackLock[]
}

interface ListExampleChunksResponse {
    examples: ExampleChunk[]
}

interface ListQualityControlWarningsResponse {
    warnings: QualityControlWarning[]
}

interface DocsetDescriptionResponse {
    docsetId: string;
    description: string;
    userDescription: string;
}

export enum DataType {
    Date = "Date",
    Number = "Number",
    Money = "Money",
    General = "General",
    ComplexType = "ComplexType"
}

interface LabelQualityControlSettings {
    labelId: string;
    description: string;
    dataType?: DataType;
    isNullable?: boolean;
    enabled: boolean;
    isUser: boolean;
}

interface LabelQualityControlRequest {
    docsetId: string;
    labelId: string;
}

interface UpdateLabelQualityControlSettingsRequest {
    docsetId: string;
    labelId: string;
    description: string;
    dataType?: DataType;
    isNullable?: boolean;
    enabled: boolean;
}

interface ModelStats {
    version: number;
    exactMatchF1Score: number;
    overlapF1Score: number;
    confidence: number;
}

interface DocSetModelStatsResponse {
    docsetId: string;
    modelStats: ModelStats[];
}

// Update selection on resize operations if necessary
startAppListening({
    matcher: frontendSlice.endpoints.resize.matchFulfilled,
    effect: (input, listenerApi) => {
        const currentSelection = listenerApi.getState().sdv.document?.selection?.id;
        const originalArgs = input.meta.arg.originalArgs;
        if (currentSelection === originalArgs.spanId) {
            const originalSpanId = SpanIdUtils.parse(originalArgs.spanId);
            const newSpanId = SpanIdUtils.create(originalArgs.fileId, originalArgs.tokenKeyRange.fromTokenKey, originalArgs.tokenKeyRange.toTokenKey, originalSpanId.subtreeId);
            listenerApi.dispatch(setSelectedId(newSpanId.value));
        }
    }
});

// Middleware for updating chunks and chunk types from other API requests
startAppListening({
    matcher: getReportsSlice().reportsApiSlice.endpoints.bindEntry.matchFulfilled,
    effect: (input, listenerApi) => {
        const label = input.payload.label;
        const docsetId = input.payload.docsetId;
        if (!!label && !!docsetId) {
            listenerApi.dispatch(frontendSlice.util.updateQueryData("getLabels", docsetId, x => {
                labelEntityAdapter.upsertOne(x, label);
            }));
        }

        // Handle example chunks update
        const spanId = input.meta.arg.originalArgs.request.spanId;
        if (!!label && !!spanId && input.meta.arg.originalArgs.request.action === BindEntryAction.Remove) {
            const exampleChunkId = createExampleChunkEntityId(label.label, spanId);
            listenerApi.dispatch(frontendSlice.util.invalidateTags([{ type: 'example-chunks', id: exampleChunkId }]));
        }
    }
});

startAppListening({
    matcher: getReportsSlice().reportsApiSlice.endpoints.labelEntry.matchFulfilled,
    effect: (input, listenerApi) => {
        const { entryId, reportId, label: targetLabel } = input.meta.arg.originalArgs;
        const reportSchema = getReportsSlice().reportsApiSlice.endpoints.getReportSchema.select(reportId)(listenerApi.getState()).data;
        const labelId = reportSchema?.entries.entities[entryId]?.labelId;
        if (!labelId) {
            return;
        }

        const clusterId = reportSchema.report.clusterId;
        const label = frontendSlice.endpoints.getLabels.select(clusterId)(listenerApi.getState()).data?.entities[labelId];

        if (!label || label.label === targetLabel) {
            return
        }

        listenerApi.dispatch(frontendSlice.util.updateQueryData("getLabels", clusterId, x => {
            labelEntityAdapter.updateOne(x, {
                id: label.id!,
                changes: {
                    label: targetLabel
                }
            });
        }));
    }
});

startAppListening({
    matcher: getReportsSlice().reportsApiSlice.endpoints.addEntry.matchFulfilled,
    effect: (input, listenerApi) => {
        const label = input.meta.arg.originalArgs.definition.title;
        const reportId = input.meta.arg.originalArgs.reportId;
        const docsetId = getReportsSlice().reportsApiSlice.endpoints.getReportSchema.select(reportId)(listenerApi.getState()).data?.report.clusterId;
        if (!docsetId || !label) {
            return;
        }

        const labelSchemaState = frontendSlice.endpoints.getLabels.select(docsetId)(listenerApi.getState()).data;
        const labelSchema = !!labelSchemaState ? selectAllLabels(labelSchemaState).find(x => x.label === label) : undefined;
        if (!labelSchema) {
            listenerApi.dispatch(frontendSlice.util.invalidateTags([{ type: "labels", id: docsetId }]));
        }
    }
});

export interface FeedbackWatermarksChangedMessage {
    docsetId: string;
};

export const {
    selectAll: selectAllLabels,
    selectById: selectLabelById,
} = labelEntityAdapter.getSelectors();

export interface GetChunkInstancesRequest {
    docsetId: string,
    fileId: string
}

export interface GetChunkInstanceRequest {
    docsetId: string,
    fileId: string,
    instanceId: string
}

interface ExampleChunksChangedMessage {
    organizationId: string;
    item: ExampleChunk;
}

interface ExampleChunksRemovedMessage {
    organizationId: string;
    docsetId: string;
    spanId: string;
    label: string;
}

const isExampleRemovedMessage = (message: ExampleChunksChangedMessage | ExampleChunksRemovedMessage): message is ExampleChunksRemovedMessage => !!(message as ExampleChunksRemovedMessage).spanId;

// When labeling a chunk, add label to recent labels
startAppListening({
    matcher: isAnyOf(frontendSlice.endpoints.labelChunk.matchFulfilled, frontendSlice.endpoints.labelChunks.matchFulfilled, frontendSlice.endpoints.addLabel.matchFulfilled),
    effect: (input, listenerApi) => {
        const docsetId = input.meta.arg.originalArgs.docsetId;
        const label = input.meta.arg.originalArgs.label;
        const currentDocsetId = listenerApi.getState().sdv.document?.docsetId;
        if (!!label && currentDocsetId === docsetId) {
            listenerApi.dispatch(sdvSlice.actions.addRecentLabel(label));
        }
    }
});

// Fetch new labels when creating a new one for an added chunk
startAppListening({
    matcher: frontendSlice.endpoints.createDocumentProperty.matchFulfilled,
    effect: (input, listenerApi) => {
        const docsetId = input.meta.arg.originalArgs.docsetId;
        const labelId = input.payload.labelId;
        // Inlined selectLabelState to avoid circular dependency with ./selectors
        const labelState = frontendSlice.endpoints.getLabels.select(docsetId)(listenerApi.getState()).data;
        if (!!labelState) {
            const hasLabel = !!selectLabelById(labelState, labelId);
            if (!hasLabel) {
                listenerApi.dispatch(frontendSlice.util.invalidateTags([{ type: "labels", id: docsetId }]));
            }
        }
    }
});

// Apply feedback locking optimistic updates to report bindings
startAppListening({
    matcher: frontendSlice.endpoints.lockFeedback.matchPending,
    effect: async (action, listenerApi) => {
        const state = listenerApi.getState();
        let operationIds: string[] = [];
        if (!!state.reportsSlice.reportId) {
            const reportsSlice = getReportsSlice();
            const schema = reportsSlice.selectPatchedReportSchemaState(state, state.reportsSlice.reportId);
            const lockItems = action.meta.arg.originalArgs.items;
            if (!!schema && !!lockItems.length) {
                for (const lockItem of lockItems) {
                    const entry = reportsSlice.selectAllEntries(schema.entries).find(x => x.labelId === lockItem.labelId);
                    if (!!entry) {
                        const folderId = createFolderIdString(action.meta.arg.originalArgs.docsetId, lockItem.fileId);
                        const operationId = listenerApi.dispatch(reportsSlice.addRemoteOperation(reportsSlice.createOperationId()));
                        operationIds.push(operationId);
                        listenerApi.dispatch(
                            reportsSlice.addBindingsOptimisticUpdate({ reportId: state.reportsSlice.reportId, fileId: lockItem.fileId },
                                (x) => {
                                    const entryBindings = x.entryBindings.entities[entry.id] ?? {
                                        entryId: entry.id,
                                        folderId: folderId,
                                        bindings: []
                                    }

                                    const bindings: ReportBinding[] = lockItem.spanIds.length > 0 ?
                                        lockItem.spanIds.map(spanId => {
                                            const providerId = entryBindings.bindings.find(b => b.spanId === spanId)?.bindingProviderId ?? BindingProviderId.User;
                                            return {
                                                id: spanId,
                                                entryId: entry.id,
                                                folderId: folderId,
                                                isConfirmed: true,
                                                userConfirmed: true,
                                                bindingProviderId: providerId,
                                                spanId: spanId
                                            }
                                        }) :
                                        [{
                                            id: `${lockItem.fileId}:${lockItem.labelId}:none`,
                                            entryId: entry.id,
                                            folderId: folderId,
                                            isConfirmed: true,
                                            userConfirmed: true,
                                            bindingProviderId: entryBindings.bindings.find(b => !b.spanId)?.bindingProviderId ?? BindingProviderId.User
                                        }];

                                    entryBindings.bindings = bindings;
                                    reportsSlice.reportEntryBindingsAdapter.upsertOne(x.entryBindings, entryBindings);
                                },
                                operationId
                            ))
                    }

                }
            }

            let result = false;
            try {
                const fulfilledPromise = listenerApi.condition(
                    (action, state) => frontendSlice.endpoints.lockFeedback.matchFulfilled(action));

                const rejectedPromise = listenerApi.condition(
                    (action, state) => frontendSlice.endpoints.lockFeedback.matchRejected(action));

                result = await Promise.race([fulfilledPromise.then(() => true), rejectedPromise.then(() => false)]);
            }
            catch (error) {
                result = false;
            }

            if (!result && !!operationIds.length) {
                const reportsSliceForCleanup = getReportsSlice();
                for (const operationId of operationIds) {
                    listenerApi.dispatch(reportsSliceForCleanup.removeRemoteOp(operationId));
                }
            }
        }
    }
})

export const handleLabelRemovedMessage = (message: LabelRemovedMessage) => (dispatch: AppDispatch) => {
    dispatch(frontendSlice.util.invalidateTags([
        { type: 'labels', id: message.docsetId },
        { type: 'feedback-locks', id: message.docsetId },
    ]))
}

export const handleReportSchemaModified = () => (dispatch: AppDispatch, getState: () => AppState) => {
    const docsetId = getState().reportsSlice.clusterId;

    dispatch(frontendSlice.util.invalidateTags([
        { type: 'labels', id: docsetId },
        { type: 'feedback-locks', id: docsetId },
    ]))
}

export const handleActiveReportForDocsetUpdate = (docsetId: string, action: (reportId: string) => void) => (dispatch: AppDispatch, getState: () => AppState) => {
    const { clusterId: reportDocsetId, reportId } = getState().reportsSlice;
    if (!reportId || docsetId !== reportDocsetId) {
        // Nothing to do - no active report, or active report is not for the given docset
        return;
    }

    action(reportId);
}